import React, { useRef, useEffect } from 'react';
import { MapContainer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import BaseMapLayer from './maps/BaseMapLayer.js';

const PIN_ICON = L.divIcon({
  className: '',
  html: `<div style="width:14px;height:14px;background:#d4af37;border:2px solid rgba(255,255,255,.85);border-radius:50%;box-shadow:0 0 10px rgba(212,175,55,.7);cursor:grab;"></div>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

function MapClickHandler({ onPick }: { onPick: (lat: number, lng: number) => void }) {
  useMapEvents({
    dblclick(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function MapFlyHandler({ lat, lng, flyKey }: { lat: number; lng: number; flyKey: number }) {
  const map = useMap();
  const prevKeyRef = useRef(flyKey);

  useEffect(() => {
    if (flyKey !== prevKeyRef.current) {
      prevKeyRef.current = flyKey;
      map.flyTo([lat, lng], Math.max(map.getZoom(), 15), { duration: 0.8 });
    }
  }, [flyKey, lat, lng, map]);

  return null;
}

interface Props {
  lat: string;
  lng: string;
  flyKey: number;
  onPick: (lat: number, lng: number) => void;
}

function LocationPickerMap({ lat, lng, flyKey, onPick }: Props) {
  const latNum = Number(lat);
  const lngNum = Number(lng);
  const hasPin = lat !== '' && lng !== '' && !isNaN(latNum) && !isNaN(lngNum);
  const pinPos: [number, number] | null = hasPin ? [latNum, lngNum] : null;

  return (
    <MapContainer
      center={pinPos ?? [0, 0]}
      zoom={14}
      style={{ height: 210, borderRadius: 6, border: '1px solid var(--bm)', cursor: 'crosshair' }}
      doubleClickZoom={false}
      zoomControl={false}
    >
      <BaseMapLayer />
      <MapClickHandler onPick={onPick} />
      {hasPin && <MapFlyHandler lat={latNum} lng={lngNum} flyKey={flyKey} />}
      {pinPos && (
        <Marker
          position={pinPos}
          icon={PIN_ICON}
          draggable
          eventHandlers={{
            dragend(e) {
              const ll = e.target.getLatLng();
              onPick(ll.lat, ll.lng);
            },
          }}
        />
      )}
    </MapContainer>
  );
}

export default LocationPickerMap;
